import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, AlertCircle } from 'lucide-react';

const ResultsDisplay = ({ result }) => {
  if (!result) return null;

  const isProfit = result?.lucroLiquido > 0;
  const isBreakEven = result?.lucroLiquido === 0;
  
  const formatCurrency = (value) => {
    const numValue = parseFloat(value);
    
    // Check for NaN or invalid
    if (!Number.isFinite(numValue)) {
      return 'R$ 0,00';
    }
    
    // Handle negative values properly
    const absValue = Math.abs(numValue);
    const formatted = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(absValue);
    
    // Add negative sign if needed
    return numValue < 0 ? `-${formatted}` : formatted;
  };

  const formatPercent = (value) => {
    const numValue = parseFloat(value);
    if (!Number.isFinite(numValue)) return '0,00%';
    return `${numValue?.toFixed(2)?.replace('.', ',')}%`;
  };

  // Calculate totals - NO Math.max, allow negatives
  const voce_gasta = (result?.custoDiretoTotal || 0) + (result?.custoEquipeTotal || 0) + (result?.custoFixosTotal || 0);
  const totalImpostos = (result?.dasTotal || 0) + (result?.darfTotal || 0);
  const total_gastos = result?.totalGastos || 0;
  const lucro = result?.lucroLiquido || 0;
  
  // Validation check
  if (Number.isNaN(total_gastos) || Number.isNaN(lucro)) {
    return (
      <div className="p-6 bg-destructive/10 border border-destructive rounded-lg">
        <AlertCircle className="h-6 w-6 text-destructive mb-2" />
        <p className="text-destructive font-semibold">Existe um campo inválido. Verifique os valores.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-xl font-bold text-foreground mb-6">Resultado do Cálculo</h3>
        
        {/* 4-LINE SUMMARY - Simple view for architects */}
        <div className="p-6 bg-primary/5 rounded-lg border-2 border-primary/20 mb-6">
          <h4 className="text-lg font-bold text-foreground mb-4">Resumo da Obra</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Cliente paga</p>
              <p className="text-xl font-bold text-foreground">{formatCurrency(result?.totalObra)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Você gasta</p>
              <p className="text-xl font-bold text-orange-600">{formatCurrency(voce_gasta)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Impostos</p>
              <p className="text-xl font-bold text-amber-600">{formatCurrency(totalImpostos)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Sobra para a empresa</p>
              <p className={`text-xl font-bold ${
                isBreakEven ? 'text-foreground' : isProfit ? 'text-success' : 'text-destructive'
              }`}>
                {formatCurrency(lucro)}
              </p>
            </div>
          </div>
        </div>

        {/* Main Result Card */}
        <div className={`p-6 rounded-lg border-2 mb-6 ${
          isBreakEven 
            ? 'bg-muted/50 border-border'
            : isProfit 
              ? 'bg-success/10 border-success' :'bg-destructive/10 border-destructive'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {isBreakEven ? (
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
              ) : isProfit ? (
                <TrendingUp className="h-8 w-8 text-success" />
              ) : (
                <TrendingDown className="h-8 w-8 text-destructive" />
              )}
              <div>
                <p className="text-sm font-medium text-muted-foreground">Resultado Final</p>
                <p className={`text-2xl font-bold ${
                  isBreakEven ? 'text-foreground' : isProfit ? 'text-success' : 'text-destructive'
                }`}>
                  {isBreakEven ? 'EMPATE' : isProfit ? 'LUCRO' : 'PREJUÍZO'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-muted-foreground">Lucro/Prejuízo</p>
              <p className={`text-3xl font-bold ${
                isBreakEven ? 'text-foreground' : isProfit ? 'text-success' : 'text-destructive'
              }`}>
                {formatCurrency(lucro)}
              </p>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                {formatPercent(result?.lucroPercent)} da receita
              </p>
            </div>
          </div>
        </div>

        {/* Breakdown Grid - Simple language */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* Receita */}
          <div className="p-4 bg-muted rounded-lg border border-border">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold text-foreground">Quanto o cliente paga</p>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(result?.totalObra)}</p>
            {result?.valorJaFaturado > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Já faturado: {formatCurrency(result?.valorJaFaturado)}
              </p>
            )}
          </div>

          {/* Custos Diretos */}
          <div className="p-4 bg-muted rounded-lg border border-border">
            <p className="text-sm font-semibold text-foreground mb-2">O que você paga na obra</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(result?.custoDiretoTotal)}</p>
            <div className="text-xs text-muted-foreground mt-2 space-y-1">
              {result?.custoEmpreiteiro > 0 && (
                <p>Empreiteiro: {formatCurrency(result?.custoEmpreiteiro)}</p>
              )}
              {result?.visitasObraTotal > 0 && (
                <p>Visitas: {formatCurrency(result?.visitasObraTotal)}</p>
              )}
              {result?.outrosCustosDiretos > 0 && (
                <p>Outros: {formatCurrency(result?.outrosCustosDiretos)}</p>
              )}
            </div>
          </div>

          {/* Custos de Equipe */}
          <div className="p-4 bg-muted rounded-lg border border-border">
            <p className="text-sm font-semibold text-foreground mb-2">Equipe (por mês)</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(result?.custoEquipeTotal)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(result?.equipeMensalPrestadores)}/mês × {result?.duracaoMeses} meses
            </p>
          </div>

          {/* Custos Fixos */}
          <div className="p-4 bg-muted rounded-lg border border-border">
            <p className="text-sm font-semibold text-foreground mb-2">Custos do escritório</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(result?.custoFixosTotal)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(result?.fixosMensaisEscritorio)}/mês × {result?.duracaoMeses} meses
            </p>
          </div>

          {/* DAS */}
          <div className="p-4 bg-muted rounded-lg border border-border">
            <p className="text-sm font-semibold text-foreground mb-2">Imposto da nota (Simples)</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(result?.dasTotal)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatPercent(result?.dasPercentualEfetivo)} sobre {result?.dasBaseCalculo === 'total_obra' ? 'total da obra' : 'valor a faturar'}
            </p>
          </div>

          {/* DARF */}
          <div className="p-4 bg-muted rounded-lg border border-border">
            <p className="text-sm font-semibold text-foreground mb-2">Imposto do pró-labore</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(result?.darfTotal)}</p>
            <div className="text-xs text-muted-foreground mt-2 space-y-1">
              <p>INSS mensal: {formatCurrency(result?.inssMensal)}</p>
              <p>IRRF mensal: {formatCurrency(result?.irrfMensal)}</p>
              <p>Total: {formatCurrency((result?.inssMensal || 0) + (result?.irrfMensal || 0))}/mês × {result?.duracaoMeses} meses</p>
            </div>
          </div>
        </div>

        {/* Conta da Obra - Detailed breakdown */}
        <div className="p-4 bg-muted/50 rounded-lg border border-border mb-6">
          <h4 className="text-lg font-bold text-foreground mb-4">Conta da Obra</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cliente paga (Receita):</span>
              <span className="font-semibold text-foreground">{formatCurrency(result?.totalObra)}</span>
            </div>
            <div className="border-t border-border pt-2 mt-2">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Custos:</p>
              <div className="flex justify-between ml-4">
                <span className="text-muted-foreground">a) Empreiteiro (mão de obra + material):</span>
                <span className="font-medium text-foreground">{formatCurrency(result?.custoEmpreiteiro)}</span>
              </div>
              <div className="flex justify-between ml-4">
                <span className="text-muted-foreground">b) Equipe mensal × {result?.duracaoMeses} meses:</span>
                <span className="font-medium text-foreground">{formatCurrency(result?.custoEquipeTotal)}</span>
              </div>
              <div className="flex justify-between ml-4">
                <span className="text-muted-foreground">c) Visitas (total):</span>
                <span className="font-medium text-foreground">{formatCurrency(result?.visitasObraTotal)}</span>
              </div>
              {result?.outrosCustosDiretos > 0 && (
                <div className="flex justify-between ml-4">
                  <span className="text-muted-foreground">d) Outras despesas:</span>
                  <span className="font-medium text-foreground">{formatCurrency(result?.outrosCustosDiretos)}</span>
                </div>
              )}
              <div className="flex justify-between ml-4">
                <span className="text-muted-foreground">e) Custos do escritório × {result?.duracaoMeses} meses:</span>
                <span className="font-medium text-foreground">{formatCurrency(result?.custoFixosTotal)}</span>
              </div>
            </div>
            <div className="border-t border-border pt-2 mt-2">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Impostos:</p>
              <div className="flex justify-between ml-4">
                <span className="text-muted-foreground">f) DAS ({formatPercent(result?.dasPercentualEfetivo)}):</span>
                <span className="font-medium text-foreground">{formatCurrency(result?.dasTotal)}</span>
              </div>
              <div className="flex justify-between ml-4">
                <span className="text-muted-foreground">g) Pró-labore (INSS + IRRF) × {result?.duracaoMeses}:</span>
                <span className="font-medium text-foreground">{formatCurrency(result?.darfTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Summary */}
        <div className="p-4 bg-muted/50 rounded-lg border-2 border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Receita Total</p>
              <p className="text-xl font-bold text-foreground">{formatCurrency(result?.totalObra)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Total de Gastos</p>
              <p className="text-xl font-bold text-destructive">{formatCurrency(total_gastos)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Lucro/Prejuízo</p>
              <p className={`text-xl font-bold ${
                isBreakEven ? 'text-foreground' : isProfit ? 'text-success' : 'text-destructive'
              }`}>
                {formatCurrency(lucro)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;